import { useState, useEffect } from 'react';
import './ContactPage.css';
import { setPageTitle, pageTitles, pageDescriptions } from '../utils/pageTitle';
import { useLanguage } from '../contexts/LanguageContext';
import { ProtectedEmail } from '../utils/emailProtection';

const ContactPage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    setPageTitle(pageTitles.contact, pageDescriptions.contact, 'contact', language);
  }, [language]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    budget: '',
    timeline: '',
    message: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [submitMessage, setSubmitMessage] = useState('');
  const [submissionId, setSubmissionId] = useState('');

  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    switch (name) {
      case 'name':
        if (!value.trim()) {
          errors.name = t('contact.validation.nameRequired');
        } else if (value.trim().length < 2) {
          errors.name = t('contact.validation.nameMinLength');
        } else {
          delete errors.name;
        }
        break;

      case 'email':
        const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
        if (!value.trim()) {
          errors.email = t('contact.validation.emailRequired');
        } else if (!emailRegex.test(value)) {
          errors.email = t('contact.validation.emailInvalid');
        } else {
          delete errors.email;
        }
        break;

      case 'phone':
        const phoneRegex = /[\+]?[0-9\s\-\(\)]{10,}/;
        if (value && !phoneRegex.test(value)) {
          errors.phone = t('contact.validation.phoneInvalid');
        } else {
          delete errors.phone;
        }
        break;

      case 'message':
        if (!value.trim()) {
          errors.message = t('contact.validation.messageRequired');
        } else if (value.trim().length < 10) {
          errors.message = t('contact.validation.messageMinLength');
        } else {
          delete errors.message;
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);

    // Update validation message in DOM
    const validationElement = document.getElementById(`${name}-validation`);
    if (validationElement) {
      if (errors[name]) {
        validationElement.textContent = errors[name];
        validationElement.className = 'validation-message error';
      } else if (value.trim() && name !== 'company' && name !== 'service' && name !== 'budget' && name !== 'timeline') {
        validationElement.textContent = t('contact.validation.looksGood');
        validationElement.className = 'validation-message success';
      } else {
        validationElement.textContent = '';
        validationElement.className = 'validation-message';
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate field on change
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous status
    setSubmitStatus(null);
    setSubmitMessage('');

    // Validate all required fields
    validateField('name', formData.name);
    validateField('email', formData.email);
    validateField('message', formData.message);

    // Check if there are any validation errors
    const hasErrors = Object.keys(validationErrors).length > 0 ||
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.message.trim();

    if (hasErrors) {
      // Focus on first error field
      const firstErrorField = document.querySelector('.form-group input:invalid, .form-group textarea:invalid');
      if (firstErrorField) {
        firstErrorField.focus();
      }
      return;
    }

    // Set loading state
    setIsSubmitting(true);

    try {
      // Prepare form data for PHP backend
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('company', formData.company.trim());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('service', formData.service);
      formDataToSend.append('budget', formData.budget);
      formDataToSend.append('timeline', formData.timeline);
      formDataToSend.append('message', formData.message.trim());
      formDataToSend.append('subject', 'Contact Form Submission');
      formDataToSend.append('language', language);

      // Submit to PHP backend
      const response = await fetch('/sendmail.php', {
        method: 'POST',
        body: formDataToSend,
        credentials: 'same-origin'
      });

      const result = await response.json();

      if (result.response === 'success') {
        // Success - both business and confirmation emails sent
        setSubmitStatus('success');
        setSubmitMessage(result.message || t('contact.form.successMessage'));
        setSubmissionId(result.submissionId || '');

        // Reset form on success
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          budget: '',
          timeline: '',
          message: ''
        });
        setValidationErrors({});

        // Clear validation messages
        document.querySelectorAll('.validation-message').forEach(el => {
          el.textContent = '';
          el.className = 'validation-message';
        });

        // Announce success to screen readers
        const successAnnouncement = document.getElementById('form-status-announcement');
        if (successAnnouncement) {
          successAnnouncement.textContent = t('contact.form.successAnnouncement');
        }

      } else {
        // Error occurred - preserve form data
        setSubmitStatus('error');
        setSubmitMessage(result.message || t('contact.form.errorGeneral'));

        // Handle specific field errors
        if (result.empty) {
          const errorField = document.getElementById(result.empty);
          if (errorField) {
            errorField.focus();
            // Add visual indication to the error field
            errorField.setAttribute('aria-invalid', 'true');
            setTimeout(() => {
              errorField.removeAttribute('aria-invalid');
            }, 5000);
          }
        }

        // Handle rate limiting with countdown
        if (result.retryAfter) {
          const minutes = Math.ceil(result.retryAfter / 60);
          setSubmitMessage(t('contact.form.rateLimitExceeded').replace('{minutes}', minutes));

          // Start countdown timer
          let remainingTime = result.retryAfter;
          const countdownInterval = setInterval(() => {
            remainingTime -= 1;
            if (remainingTime <= 0) {
              clearInterval(countdownInterval);
              setSubmitMessage(t('contact.form.retrySubmission'));
            } else {
              const mins = Math.ceil(remainingTime / 60);
              setSubmitMessage(t('contact.form.rateLimitExceeded').replace('{minutes}', mins));
            }
          }, 1000);
        }

        // Announce error to screen readers with more detail
        const errorAnnouncement = document.getElementById('form-status-announcement');
        if (errorAnnouncement) {
          if (result.retryAfter) {
            errorAnnouncement.textContent = t('contact.form.rateLimitWait');
          } else if (result.empty) {
            errorAnnouncement.textContent = t('contact.form.submissionFailedCheck').replace('{field}', result.empty);
          } else {
            errorAnnouncement.textContent = t('contact.form.submissionFailedGeneral');
          }
        }

        // Scroll to error message for better visibility
        const statusMessage = document.querySelector('.form-status-message');
        if (statusMessage) {
          statusMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');

      // Provide specific error messages based on error type
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setSubmitMessage(t('contact.form.errorNetwork'));
      } else if (error.name === 'AbortError') {
        setSubmitMessage(t('contact.form.errorTimeout'));
      } else {
        setSubmitMessage(t('contact.form.errorUnexpected'));
      }

      // Announce network error to screen readers with more context
      const errorAnnouncement = document.getElementById('form-status-announcement');
      if (errorAnnouncement) {
        errorAnnouncement.textContent = t('contact.form.networkErrorPreserved');
      }

      // Scroll to error message
      setTimeout(() => {
        const statusMessage = document.querySelector('.form-status-message');
        if (statusMessage) {
          statusMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: '📧',
      title: t('contact.contactInfo.email'),
      value: 'info@radixtechs.com',
      link: 'mailto:info@radixtechs.com',
      isEmail: true
    },
    {
      icon: '📞',
      title: t('contact.contactInfo.phone'),
      value: '972-92337772+',
      link: 'tel:+972923377772'
    },
    {
      icon: '📍',
      title: t('contact.contactInfo.location'),
      value: 'West-Bank, Palestine',
      link: '#'
    },
    {
      icon: '🕒',
      title: t('contact.contactInfo.businessHours'),
      value: t('contact.contactInfo.businessHoursValue'),
      link: '#'
    }
  ];

  const services = [
    { value: 'devops-automation', label: t('contact.services.devopsAutomation') },
    { value: 'cloud-solutions', label: t('contact.services.cloudSolutions') },
    { value: 'managed-services', label: t('contact.services.managedServices') },
    { value: 'consulting', label: t('contact.services.consulting') },
    { value: 'mobile-development', label: t('contact.services.mobileDevelopment') },
    { value: 'quality-assurance', label: t('contact.services.qualityAssurance') },
    { value: 'ai-ml-solutions', label: t('contact.services.aiMlSolutions') },
    { value: 'web-development', label: t('contact.services.webDevelopment') },
    { value: 'other', label: t('contact.services.customSolution') }
  ];

  const budgetRanges = [
    { value: 'under-10k', label: t('contact.budgetRanges.under10k') },
    { value: '10k-25k', label: t('contact.budgetRanges.10k25k') },
    { value: '25k-50k', label: t('contact.budgetRanges.25k50k') },
    { value: '50k-100k', label: t('contact.budgetRanges.50k100k') },
    { value: 'over-100k', label: t('contact.budgetRanges.over100k') },
    { value: 'discuss', label: t('contact.budgetRanges.discuss') }
  ];

  const timelines = [
    { value: 'asap', label: t('contact.timelines.asap') },
    { value: '1-3-months', label: t('contact.timelines.1to3months') },
    { value: '3-6-months', label: t('contact.timelines.3to6months') },
    { value: '6-12-months', label: t('contact.timelines.6to12months') },
    { value: 'over-12-months', label: t('contact.timelines.over12months') },
    { value: 'flexible', label: t('contact.timelines.flexible') }
  ];

  return (
    <div className="contact-page">
      <section className="contact-hero section">
        <div className="container">
          <div className="contact-hero-content">
            <h1 className="page-title">{t('contact.pageTitle')}</h1>
            <p className="contact-hero-description">
              {t('contact.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      <section className="contact-main section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-form-section">
              <h2 className="form-title">{t('contact.formTitle')}</h2>

              {/* Status announcement for screen readers */}
              <div
                id="form-status-announcement"
                className="sr-only"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              ></div>

              {/* Status message display */}
              {submitStatus && (
                <div
                  className={`form-status-message ${submitStatus}`}
                  role="alert"
                  aria-live="assertive"
                >
                  <div dangerouslySetInnerHTML={{ __html: submitMessage }} />
                  {submissionId && (
                    <div className="submission-id">
                      {t('contact.form.referenceId')} {submissionId}
                    </div>
                  )}
                  {submitStatus === 'success' && (
                    <div className="next-steps">
                      <strong>{t('contact.form.whatHappensNext')}</strong>
                      <ul>
                        <li>{t('contact.form.confirmationEmail')}</li>
                        <li>{t('contact.form.teamReview')}</li>
                        <li>{t('contact.form.contactDiscuss')}</li>
                      </ul>
                    </div>
                  )}
                  {submitStatus === 'error' && !submitMessage.includes('Rate limit') && (
                    <button
                      type="button"
                      className="retry-btn"
                      onClick={() => {
                        setSubmitStatus(null);
                        setSubmitMessage('');
                      }}
                      aria-label={t('contact.form.dismissError')}
                    >
                      {t('contact.form.tryAgain')}
                    </button>
                  )}
                </div>
              )}

              <form
                className={`contact-form ${isSubmitting ? 'submitting' : ''}`}
                onSubmit={handleSubmit}
                aria-busy={isSubmitting}
              >
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t('contact.fullName')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.fullNamePlaceholder')}
                      aria-describedby="name-validation"
                      aria-required="true"
                      minLength="2"
                      maxLength="50"
                      className="input-color-animated"
                      disabled={isSubmitting}
                    />
                    <div id="name-validation" className="validation-message" role="alert" aria-live="polite"></div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">{t('contact.emailAddress')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.emailPlaceholder')}
                      aria-describedby="email-validation"
                      aria-required="true"
                      pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                      className="input-color-animated"
                      disabled={isSubmitting}
                    />
                    <div id="email-validation" className="validation-message" role="alert" aria-live="polite"></div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">{t('contact.company')}</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={t('contact.companyPlaceholder')}
                      aria-describedby="company-validation"
                      maxLength="100"
                      className="input-color-animated"
                      disabled={isSubmitting}
                    />
                    <div id="company-validation" className="validation-message" role="alert" aria-live="polite"></div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">{t('contact.phoneNumber')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('contact.phonePlaceholder')}
                      aria-describedby="phone-validation"
                      pattern="[\+]?[0-9\s\-\(\)]{10,}"
                      className="input-color-animated"
                      disabled={isSubmitting}
                    />
                    <div id="phone-validation" className="validation-message" role="alert" aria-live="polite"></div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="service">{t('contact.serviceNeeded')}</label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      aria-describedby="service-validation"
                      disabled={isSubmitting}
                    >
                      <option value="">{t('contact.selectService')}</option>
                      {services.map((service, index) => (
                        <option key={index} value={service.value}>{service.label}</option>
                      ))}
                    </select>
                    <div id="service-validation" className="validation-message" role="alert" aria-live="polite"></div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="budget">{t('contact.budgetRange')}</label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      aria-describedby="budget-validation"
                      disabled={isSubmitting}
                    >
                      <option value="">{t('contact.selectBudget')}</option>
                      {budgetRanges.map((budget, index) => (
                        <option key={index} value={budget.value}>{budget.label}</option>
                      ))}
                    </select>
                    <div id="budget-validation" className="validation-message" role="alert" aria-live="polite"></div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="timeline">{t('contact.projectTimeline')}</label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    aria-describedby="timeline-validation"
                    disabled={isSubmitting}
                  >
                    <option value="">{t('contact.selectTimeline')}</option>
                    {timelines.map((timeline, index) => (
                      <option key={index} value={timeline.value}>{timeline.label}</option>
                    ))}
                  </select>
                  <div id="timeline-validation" className="validation-message" role="alert" aria-live="polite"></div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contact.projectDescription')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder={t('contact.projectPlaceholder')}
                    aria-describedby="message-validation"
                    aria-required="true"
                    minLength="10"
                    maxLength="1000"
                    className="input-color-animated"
                    disabled={isSubmitting}
                  ></textarea>
                  <div id="message-validation" className="validation-message" role="alert" aria-live="polite"></div>
                </div>

                <button
                  type="submit"
                  className="submit-btn btn-color-shift"
                  disabled={isSubmitting}
                  aria-describedby="submit-status"
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner" aria-hidden="true"></span>
                      <span>{t('contact.form.sending')}</span>
                    </>
                  ) : (
                    <>
                      {t('contact.sendMessage')}
                      <span className="btn-icon">🚀</span>
                    </>
                  )}
                </button>

                {/* Submit button status for screen readers */}
                <div id="submit-status" className="sr-only" aria-live="polite">
                  {isSubmitting ? t('contact.form.formSubmitting') : ''}
                </div>
              </form>
            </div>

            <div className="contact-info-section">
              <h2 className="info-title">{t('contact.contactInformation')}</h2>
              <div className="contact-info-grid">
                {contactInfo.map((info, index) => (
                  <div key={index} className="contact-info-item">
                    <div className="info-icon">{info.icon}</div>
                    <div className="info-content">
                      <h3 className="info-label">{info.title}</h3>
                      {info.isEmail ? (
                        <ProtectedEmail
                          email={info.value}
                          displayText={info.value}
                          className="info-value"
                        />
                      ) : info.link !== '#' ? (
                        <a href={info.link} className="info-value">{info.value}</a>
                      ) : (
                        <span className="info-value">{info.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="social-section">
                <h3 className="social-title">{t('contact.followUs')}</h3>
                <div className="social-links">
                  <div className='social-icons'>
                    <a href="https://linkedin.com/company/radix-techs" className="social-link" aria-label="LinkedIn">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>
                    <a href="https://facebook.com/RadixTechs" className="social-link" aria-label="Facebook">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              <div className="response-time">
                <div className="response-card">
                  <div className="response-icon">⚡</div>
                  <div className="response-content">
                    <h3>{t('contact.quickResponse')}</h3>
                    <p>{t('contact.responseDescription')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
